import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, ExternalLink, Edit } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { getPublicUrl } from "@/utils/publicUrl";

interface ShareLinksCardProps {
  bookingUrl?: string;
}

export function ShareLinksCard({ bookingUrl }: ShareLinksCardProps) {
  const navigate = useNavigate();

  const publicUrl = bookingUrl ? getPublicUrl(bookingUrl) : null;

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Failed to copy link");
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold">Your Public Page</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-foreground">Public link</label>
          {publicUrl && (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => window.open(publicUrl, "_blank")}
                title="Open public page"
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => copyToClipboard(publicUrl)}
                title="Copy link"
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs"
                onClick={() => navigate("/dashboard/linkinbio")}
                title="Customize page"
              >
                <Edit className="h-3 w-3 mr-1" />
                Customize
              </Button>
            </div>
          )}
        </div>
        {publicUrl ? (
          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <code className="text-sm text-blue-800 font-mono flex-1 truncate">{publicUrl}</code>
          </div>
        ) : (
          <div className="p-3 bg-muted border border-border rounded-lg">
            <p className="text-sm text-muted-foreground">Complete setup to enable your public page</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
